import { lazy, Suspense, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { SigningMethodSelect } from "./SigningMethodSelect";
import { EmailInput } from "./EmailInput";

interface SigningRequestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: {
    id: string;
    title: string;
    content: string;
  } | null;
}

export function SigningRequestDialog({ isOpen, onClose, document }: SigningRequestDialogProps) {
  const [signerEmail, setSignerEmail] = useState("");
  const [signingMethod, setSigningMethod] = useState<"email" | "direct">("email");
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const handleInitiateSigning = async () => {
    if (!document || !user) return;

    setIsLoading(true);
    try {
      console.log("Initiating signing request for document:", document.id);
      
      const { data: response, error } = await supabase.functions.invoke('criipto-integration', {
        body: {
          action: 'initiate_signing',
          documentId: document.id,
          content: document.content,
          signerEmail: signingMethod === 'email' ? signerEmail : null,
          signingMethod: signingMethod
        }
      });

      if (error) throw error;

      console.log("Signing request created:", response);

      if (signingMethod === 'email') {
        toast.success("Signeringsforespørsel sendt", {
          description: `En e-post med signeringslenke er sendt til ${signerEmail}`
        });
      } else {
        if (response.signatureOrder?.signingUrl) {
          window.open(response.signatureOrder.signingUrl, '_blank');
          toast.success("BankID-signering initiiert", {
            description: "Følg instruksjonene i det nye vinduet for å fullføre signeringen"
          });
        }
      }
      
      onClose();
      setSignerEmail("");
      setSigningMethod("email");
    } catch (error) {
      console.error('Error initiating signing:', error);
      toast.error("Kunne ikke opprette signeringsforespørsel", {
        description: "Vennligst prøv igjen senere."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Send dokument til signering</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <SigningMethodSelect
            signingMethod={signingMethod}
            onMethodChange={setSigningMethod}
            disabled={isLoading}
          />

          {signingMethod === "email" && (
            <Suspense fallback={<div className="h-[76px] flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>}>
              <EmailInput
                email={signerEmail}
                onChange={setSignerEmail}
                disabled={isLoading}
              />
            </Suspense>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleInitiateSigning}
            disabled={
              isLoading || 
              (signingMethod === "email" && !signerEmail)
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {signingMethod === "email" ? 'Sender...' : 'Starter BankID...'}
              </>
            ) : (
              signingMethod === "email" ? 'Send til signering' : 'Start BankID-signering'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}